import { MeResponse } from 'components/login/types';
import { swrFetcher, UserContext } from 'contexts/user';
import { fetchOwnerRemixes } from 'lib/api/community_api';
import { useCallback, useContext, useEffect, useState } from 'react';
import { VoidCallback } from 'types/callbacks';
import { CommunityShotItem } from 'types/community';
import AlreadyRemixedModal from './AlreadyRemixedModal';
import StandardRemixModal from './StandardRemixModal';
import * as analytics from 'lib/analytics';
import useSWR from 'swr';
import { TeamResponse } from 'lib/teams';

type Props = {
  display: boolean;
  onClose: VoidCallback;
  shotItem: CommunityShotItem;
};

function useHasAlreadyRemixed(communityRevisionId: number) {
  const { user } = useContext(UserContext);
  const [hasAlreadyRemixed, setHadAlreadyRemixed] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const checkHasAlreadyRemixed = useCallback(async () => {
    const remixes = await fetchOwnerRemixes(communityRevisionId);
    if (remixes.length > 0 && isMounted) {
      setHadAlreadyRemixed(true);
    }
  }, [communityRevisionId, isMounted]);

  useEffect(() => {
    if (isMounted && user && user.signedIn) {
      checkHasAlreadyRemixed();
    }
  }, [user?.signedIn, communityRevisionId, isMounted]);

  return { hasAlreadyRemixed, refresh: checkHasAlreadyRemixed };
}

type ModalState = 'learn_more_modal' | 'already_remixed_modal';

function getModalState(
  user?: MeResponse,
  hasAlreadyRemixed?: boolean
): ModalState {
  if (hasAlreadyRemixed) {
    return 'already_remixed_modal';
  } else {
    return 'learn_more_modal';
  }
}

export default function RemixModal({ display, onClose, shotItem }: Props) {
  const { user } = useContext(UserContext);
  const { data: teams } = useSWR<TeamResponse[]>('/api/teams', swrFetcher);

  const { hasAlreadyRemixed, refresh } = useHasAlreadyRemixed(
    shotItem.communityRevisionId
  );

  useEffect(() => {
    if (display) {
      const modalState = getModalState(user, hasAlreadyRemixed);
      analytics.clickCommunityRemix(
        shotItem.communityPostId,
        shotItem.fileId,
        shotItem.title,
        shotItem.isRemix,
        modalState
      );
    }
  }, [user?.isPaid, hasAlreadyRemixed, display]);

  const modalState = getModalState(user, hasAlreadyRemixed);
  if (teams === undefined || teams.length === undefined) {
    return <></>;
  }
  switch (modalState) {
    case 'already_remixed_modal':
      return (
        <AlreadyRemixedModal
          display={display}
          onClose={onClose}
          onCreate={refresh}
          shotItem={shotItem}
          teams={teams}
        />
      );
    default:
      return (
        <StandardRemixModal
          display={display}
          onClose={onClose}
          onCreate={refresh}
          shotItem={shotItem}
          teams={teams}
        />
      );
  }
}
